import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  // Configure axios defaults
  useEffect(() => {
    if (accessToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [accessToken]);

  // Helper to decode JWT token
  const decodeToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  // Check for existing token on mount
  useEffect(() => {
    // First try to get tenant-scoped token, then fallback to basic token
    const tenantToken = localStorage.getItem('tenantAccessToken');
    const basicToken = localStorage.getItem('accessToken');
    const token = tenantToken || basicToken;
    
    
    if (token) {
      setAccessToken(token);
      // Decode token to extract user info including role
      const decoded = decodeToken(token);
      if (decoded) {
        setUser({
          email: decoded.email,
          role: decoded.role || 'user',
          name: decoded.name || decoded.email
        });
      }
      // Skip verification on initial load - trust the stored token
      // Token will be verified when making actual API calls
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      
      if (response.data.success) {
        setUser(response.data.user);
        setAccessToken(token);
      } else {
        // Token invalid, try to refresh
        await refresh();
      }
    } catch (error) {
      
      if (error.response?.status === 401) {
        // Token expired, try to refresh
        await refresh();
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password
      }, {
        withCredentials: true // Important for cookies
      });

      if (response.data.success) {
        const { accessToken, user } = response.data;
        setAccessToken(accessToken);
        localStorage.setItem('accessToken', accessToken);
        // Decode token to get role if not in user object
        const decoded = decodeToken(accessToken);
        setUser({
          ...user,
          role: user?.role || decoded?.role || 'user'
        });
        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const refresh = React.useCallback(async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {}, {
        withCredentials: true
      });


      if (response.data.success) {
        const { accessToken } = response.data;
        setAccessToken(accessToken);
        localStorage.setItem('accessToken', accessToken);
        // Decode token to update user role
        const decoded = decodeToken(accessToken);
        if (decoded) {
          setUser(prev => ({
            ...prev,
            email: decoded.email,
            role: decoded.role || prev?.role || 'user',
            name: decoded.name || prev?.name || decoded.email
          }));
        }
        return { success: true };
      } else {
        logout();
        return { success: false };
      }
    } catch (error) {
      logout();
      return { success: false };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
    } finally {
      setAccessToken(null);
      setUser(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('tenantAccessToken');
    }
  };

  const setTenantContext = async (tenantId, tenantKey) => {
    try {
      
      const response = await axios.post(`${API_BASE_URL}/api/auth/set-tenant`, {
        tenantId,
        tenantKey
      }, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });


      if (response.data.success) {
        const { accessToken: tenantToken } = response.data;
        localStorage.setItem('tenantAccessToken', tenantToken);
        // Also update the main accessToken state to use the tenant-scoped token
        setAccessToken(tenantToken);
        // Decode token to update user role
        const decoded = decodeToken(tenantToken);
        if (decoded) {
          setUser(prev => ({
            ...prev,
            email: decoded.email,
            role: decoded.role || prev?.role || 'user',
            name: decoded.name || prev?.name || decoded.email
          }));
        }
        return { success: true, token: tenantToken };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to set tenant context'
      };
    }
  };

  // Computed properties for admin access
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  const value = {
    accessToken,
    user,
    loading,
    login,
    logout,
    refresh,
    setTenantContext,
    isAdmin,
    isSuperAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
